import type {
  ApiErrorBody,
  MarketSignal,
  WalletHistoryResponse,
  WalletSummary,
} from './types';
import type { BankrollLedger, LedgerRules } from './bankrollLedger';
import type { Btc15LiveResponse, Btc15PredictorResponse } from './btc15Types';
import type { AutoFightSlate } from './fightAutoPicks';
import type { PassBar, PriceRuleSpec, PriceRuleSummary } from './btc15PriceRules';
import type { EthHourlySummary } from './ethHourlyRule';
import type { ForwardPassBar, ForwardRuleSummary } from './forwardRuleSummary';
import type { PooledHourlyBar, PooledHourlySummary } from './hourlyPooledRule';
import type { RiptideCheckIssues, RiptideChecksSummary } from './riptideRule';
import type {
  CRASH_BOUNCE_COSTS,
  CRASH_BOUNCE_PASS_BAR,
  CRASH_BOUNCE_RULE,
  CrashBounceSummary,
  CrashTrade,
  CrashWatchRow,
} from './crashBounce';
import type {
  BREAKOUT_BACKTEST,
  BREAKOUT_COSTS,
  BREAKOUT_PASS_BAR,
  BREAKOUT_RULE,
  BreakoutOutcome,
  BreakoutSummary,
  BreakoutTrade,
  BreakoutWatchRow,
} from './breakoutWatch';
import type {
  RUG_RULES,
  RugChain,
  RugGroup,
  RugOutcome,
  RugReport,
  RugTrackSummary,
} from './rugCheck';
import type { BENCHMARK_BACKTEST, BENCHMARK_RULE, BenchmarkResult } from './benchmark';
import type {
  LISTING_COSTS,
  LISTING_PASS_BAR,
  LISTING_RULE,
  Listing,
  ListingSummary,
} from './listingRadar';
import type { FightLogSummary } from './fightResearchLog';
import type {
  MARTINGALE_PASS_BAR,
  MARTINGALE_RULES,
  MartingaleBust,
  MartingaleRecentBet,
  MartingaleSummary,
} from './btc15Martingale';
import type {
  FinishedMarket,
  LIP_PASS_BAR,
  LIP_POLICY,
  LipBucket,
  LipBucketSummary,
  LipDay,
  LipSeries,
} from './liquidityRewards';
import type { QuotingStrategy } from './btc15Maker';
import type { MAKER_PASS_BAR, MakerForwardSummary } from './btc15MakerForward';
import type { Btc15History } from './btc15History';
import type { FittedModelsRecord } from './fittedModels';
import type { ModelHistory } from './btc15ModelHistory';
import type { ModelVersion } from './modelVersion';
import type { TakeProfitHistory } from './btc15TakeProfitHistory';
import type { ShadowExitPolicyReport } from './shadowExitPolicyReport';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const retryAfterHeader = response.headers.get('Retry-After');
    throw new ApiClientError(
      body?.error?.message ?? `Request failed with status ${response.status}.`,
      body?.error?.code ?? 'UNKNOWN_ERROR',
      response.status,
      retryAfterHeader ? Number(retryAfterHeader) : null,
    );
  }
  return response.json();
}

export function getWalletSummary(
  address: string,
  label?: string,
): Promise<WalletSummary> {
  const params = new URLSearchParams({ address });
  if (label) params.set('label', label);
  return getJson(`/api/wallet-summary?${params.toString()}`);
}

export function getWalletHistory(
  address: string,
  options: { cursor?: string | null; limit?: number } = {},
): Promise<WalletHistoryResponse> {
  const params = new URLSearchParams({ address });
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.limit) params.set('limit', String(options.limit));
  return getJson(`/api/wallet-history?${params.toString()}`);
}

export interface MarketSignalParams {
  days?: number;
  emaFast?: number;
  emaSlow?: number;
  rsiPeriod?: number;
  overbought?: number;
  oversold?: number;
  suggestedUsdAmount?: number;
}

export function getMarketSignal(options: MarketSignalParams = {}): Promise<MarketSignal> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(options)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return getJson(`/api/market-signal${query ? `?${query}` : ''}`);
}

export interface HealthResponse {
  status: string;
  version: string;
  deployCommit: string;
  timestamp: string;
}

export function getHealth(): Promise<HealthResponse> {
  return getJson('/api/health');
}

export function getBtc15Predictor(): Promise<Btc15PredictorResponse> {
  return getJson('/api/btc15-predictor');
}

export function getBtc15Live(): Promise<Btc15LiveResponse> {
  return getJson('/api/btc15-live');
}

/** The history the scheduled tracker records server-side every 15 minutes. */
export function getBtc15History(): Promise<Btc15History> {
  return getJson('/api/btc15-history');
}

/** Whatever V2/V3 models the daily scheduled refit has most recently fit, or nulls if neither has enough live data yet. */
export function getBtc15FittedModels(): Promise<FittedModelsRecord> {
  return getJson('/api/btc15-fitted-models');
}

/** The V2 or V3 track record the scheduled tracker records server-side every 15 minutes (see src/lib/btc15ModelHistory.ts). */
export function getBtc15ModelHistory(version: ModelVersion): Promise<ModelHistory> {
  return getJson(`/api/btc15-model-history?version=${version}`);
}

/** "V1 + take-profit"'s server-authoritative track record (see src/lib/btc15TakeProfitHistory.ts). */
export function getBtc15TakeProfitHistory(): Promise<TakeProfitHistory> {
  return getJson('/api/btc15-takeprofit-history');
}

/** Retrospective comparison of shadowed exit policies against real settled rounds (see src/lib/shadowExitPolicyReport.ts). Purely informational — never changes which policy is actually live. */
export function getBtc15ShadowExitPolicyReport(): Promise<ShadowExitPolicyReport> {
  return getJson('/api/btc15-shadow-exit-policy-report');
}

export function getFightAutoPicks(): Promise<AutoFightSlate> {
  return getJson('/api/fight-auto-picks');
}

/** How the fight page's review flags and the odds have done on settled fights. */
export function getFightTrackRecord(): Promise<{ summary: FightLogSummary }> {
  return getJson('/api/fight-research-log?summary=1');
}

export interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
}

/** Whether this deploy has a VAPID keypair configured, and the public key to subscribe with if so. */
export function getPushConfig(): Promise<PushConfig> {
  return getJson('/api/push-config');
}

export interface PriceRulesResponse {
  hypothetical: true;
  registeredAt: string;
  stakeDollars: number;
  passBar: PassBar;
  rules: PriceRuleSpec[];
  lastRunMs: number | null;
  summary: PriceRuleSummary[];
}

/** The pre-registered price-ladder rules' forward paper test (hypothetical only). */
export function getBtc15PriceRules(): Promise<PriceRulesResponse> {
  return getJson('/api/btc15-price-rules');
}

export interface EthHourlyRuleResponse {
  hypothetical: true;
  registeredAt: string;
  stakeDollars: number;
  passBar: PassBar;
  lastRunMs: number | null;
  summary: EthHourlySummary;
}

/** The ETH hourly rule's forward paper test (hypothetical only). */
export function getEthHourlyRule(): Promise<EthHourlyRuleResponse> {
  return getJson('/api/eth-hourly-rule');
}

export interface HourlyPooledRuleResponse {
  hypothetical: true;
  registeredAt: string;
  stakeDollars: number;
  passBar: PooledHourlyBar;
  lastRunMs: number | null;
  summary: PooledHourlySummary;
}

/** The pooled hourly rule's forward paper test (hypothetical only). */
export function getHourlyPooledRule(): Promise<HourlyPooledRuleResponse> {
  return getJson('/api/hourly-pooled-rule');
}

export interface RiptideRuleResponse {
  hypothetical: true;
  registeredAt: string;
  stakeDollars: number;
  passBar: ForwardPassBar;
  lastRunMs: number | null;
  /** How many rounds the logger checked; null until its first check. */
  checks?: RiptideChecksSummary | null;
  /** Why minute-1 looks failed, and how many were recovered; null until one fails. */
  checkIssues?: RiptideCheckIssues | null;
  summary: ForwardRuleSummary;
}

/** Riptide's forward paper test (hypothetical only). */
export function getRiptideRule(): Promise<RiptideRuleResponse> {
  return getJson('/api/riptide-rule');
}

export interface Btc15BankrollResponse {
  hypothetical: true;
  generatedAt: string;
  rules: LedgerRules;
  /** Null until the scheduled ledger function's first run. */
  ledger: BankrollLedger | null;
}

/** The permanent paper-bankroll ledger: lifetime totals and numbered runs (hypothetical only). */
export function getBtc15Bankroll(): Promise<Btc15BankrollResponse> {
  return getJson('/api/btc15-bankroll');
}

export interface CrashBounceResponse {
  hypothetical: true;
  registeredAt: string;
  rule: typeof CRASH_BOUNCE_RULE;
  costs: typeof CRASH_BOUNCE_COSTS;
  passBar: typeof CRASH_BOUNCE_PASS_BAR;
  stakeDollars: number;
  lastRunMs: number | null;
  lastFailures: string[];
  summary: CrashBounceSummary;
  watchlist: CrashWatchRow[];
  /** Closed trades, newest first. */
  trades: CrashTrade[];
}

/** Crash Bounce's forward paper test (hypothetical only). */
export function getCrashBounce(): Promise<CrashBounceResponse> {
  return getJson('/api/crash-bounce');
}

export interface MakerRecentRound {
  ticker: string;
  closeMs: number;
  result: 'yes' | 'no';
  trades: number;
  settlementCents: number | null;
  optimistic: { fills: number; pnl: number };
  pessimistic: { fills: number; inventory: number; pnl: number };
}

export interface MakerForwardResponse {
  hypothetical: true;
  strategyId: string;
  strategy: QuotingStrategy;
  forwardStart: string;
  passBar: typeof MAKER_PASS_BAR;
  lastRunMs: number | null;
  lastFailures: string[];
  summary: MakerForwardSummary;
  /** Newest first. */
  recent: MakerRecentRound[];
}

export interface LiquidityTrackedRow {
  ticker: string;
  series: string;
  bucket: LipBucket;
  phase: 'quoting' | 'awaiting';
  endMs: number;
  rewardDollars: number;
  ratePerHour: number;
  samples: number;
  qualifyingSamples: number;
  /** Pessimistic ledger. */
  reward: number;
  fills: number;
  /** Net contracts, YES minus NO. */
  net: number;
  liquidation: number;
  optimisticReward: number;
}

export interface LiquidityCandidateRow {
  ticker: string;
  series: string;
  bucket: LipBucket;
  endMs: number;
  rewardDollars: number;
  ratePerHour: number;
}

export interface LiquidityRewardsResponse {
  hypothetical: true;
  policyId: string;
  policy: typeof LIP_POLICY;
  registeredAt: string;
  passBar: typeof LIP_PASS_BAR;
  lastRunMs: number | null;
  lastRefreshMs: number | null;
  lastFailures: string[];
  tapeTruncated: number;
  buckets: LipBucketSummary[];
  days: LipDay[];
  series: LipSeries[];
  tracked: LiquidityTrackedRow[];
  candidates: LiquidityCandidateRow[];
  /** Newest first. */
  recent: FinishedMarket[];
}

/** Kalshi liquidity-rewards census, a forward paper test (hypothetical only). */
export function getLiquidityRewards(): Promise<LiquidityRewardsResponse> {
  return getJson('/api/liquidity-rewards');
}

export interface MartingaleResponse {
  hypothetical: true;
  rulesId: string;
  rules: typeof MARTINGALE_RULES;
  registeredAt: string;
  passBar: typeof MARTINGALE_PASS_BAR;
  lastRunMs: number | null;
  lastFailures: string[];
  skippedNoQuote: number;
  skippedNoResult: number;
  /** Always UP, then always DOWN. Busts and bets newest first. */
  sides: {
    summary: MartingaleSummary;
    busts: MartingaleBust[];
    recent: MartingaleRecentBet[];
  }[];
}

/** Martingale paper test on BTC 15m (hypothetical only). */
export function getMartingale(): Promise<MartingaleResponse> {
  return getJson('/api/btc15-martingale');
}

/** BTC 15m market-making forward paper test (hypothetical only). */
export function getMakerForward(): Promise<MakerForwardResponse> {
  return getJson('/api/btc15-maker-forward');
}

export interface BreakoutWatchResponse {
  hypothetical: true;
  registeredAt: string;
  rule: typeof BREAKOUT_RULE;
  costs: typeof BREAKOUT_COSTS;
  passBar: typeof BREAKOUT_PASS_BAR;
  backtest: typeof BREAKOUT_BACKTEST;
  stakeDollars: number;
  lastRunMs: number | null;
  lastFailures: string[];
  summary: BreakoutSummary;
  watchlist: BreakoutWatchRow[];
  /** Closed trades, newest first. */
  trades: BreakoutTrade[];
  /** Finished 7-day outcomes, newest first. */
  outcomes: BreakoutOutcome[];
}

/** Breakout Watch's forward paper test (hypothetical only). */
export function getBreakoutWatch(): Promise<BreakoutWatchResponse> {
  return getJson('/api/breakout-watch');
}

export interface ListingRadarResponse {
  hypothetical: true;
  registeredAt: string;
  rule: typeof LISTING_RULE;
  costs: typeof LISTING_COSTS;
  passBar: typeof LISTING_PASS_BAR;
  stakeDollars: number;
  seededAtMs: number | null;
  knownProducts: number;
  lastRunMs: number | null;
  lastError: string | null;
  summary: ListingSummary;
  /** Announced (not yet tradable) and open listings. */
  active: Listing[];
  /** Closed listings, newest first. */
  closed: Listing[];
}

/** The Coinbase listing radar's forward paper test (hypothetical only). */
export function getListingRadar(): Promise<ListingRadarResponse> {
  return getJson('/api/listing-radar');
}

export interface RugCheckResponse {
  report: RugReport;
  /** This lookup was added to the track record just now. */
  tracked: boolean;
  /** The token qualifies for tracking (a pair with $1k+ liquidity). */
  trackable: boolean;
}

/** Red flags for one token (read-only lookup). */
export function getRugCheck(chain: RugChain, address: string): Promise<RugCheckResponse> {
  const params = new URLSearchParams({ chain, address });
  return getJson(`/api/rug-check?${params.toString()}`);
}

export interface RugResolvedRow {
  chain: RugChain;
  address: string;
  symbol: string | null;
  lookedUpMs: number;
  group: RugGroup;
  critical: number;
  warnings: number;
  outcome: RugOutcome;
  liquidityUsd: number;
  liquidityUsdLater: number | null;
}

export interface RugCheckRecordResponse {
  registeredAt: string;
  rules: typeof RUG_RULES;
  lastRunMs: number | null;
  summary: RugTrackSummary;
  /** Newest first. */
  resolved: RugResolvedRow[];
}

/** How well Rug Check's flags have predicted rugs so far. */
export function getRugCheckRecord(): Promise<RugCheckRecordResponse> {
  return getJson('/api/rug-check-record');
}

export interface BenchmarkResponse {
  hypothetical: true;
  rule: typeof BENCHMARK_RULE;
  backtest: typeof BENCHMARK_BACKTEST;
  /** Null before the first close after the start date. */
  result: BenchmarkResult | null;
  lastRunMs: number | null;
  lastError: string | null;
}

/** The 60/40 index portfolio the paper tests are measured against. */
export function getBenchmark(): Promise<BenchmarkResponse> {
  return getJson('/api/benchmark');
}
